// What a finished workout looks like when you send it to a mate: a few lines
// of text for WhatsApp and the facts the picture card is drawn from.
//
// Deliberately a different thing from Copy Summary (sessionShare.js), which is
// the full monospace breakdown of every step for your own notes. A message to
// a friend wants the headline: what it was, how long, how hard, and a graph.
//
// Pure, so it can be tested without a canvas or a browser.

import { rpeLabel } from "../config/rpe";
import { zoneColourFor, zoneFloors } from "./heartRateZones";
import { phaseKind, phaseSegments } from "./telemetry/phases";

// Points on the card's graph. Enough to show intervals in a 40-minute session
// without the line turning to fuzz at phone size.
export const GRAPH_POINTS = 120;

// Less heart rate than this and the line is mostly gaps: draw something else.
const MIN_SERIES_SAMPLES = 30;

const titleOf = (session) =>
  session?.plan_data?.label || session?.workout_type || "Workout";

/** "Mon 2 Nov" in Australian order. */
export const shareDate = (dateStr) => {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-AU", {
    weekday: "short",
    day: "numeric",
    month: "short",
  }).replace(",", "");
};

/** "42 min", "1 h 05 min". */
export const shareMinutes = (min) => {
  const m = Math.round(Number(min) || 0);
  if (m < 1) return null;
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, "0")} min`;
};

/** "6.2 km", "850 m". */
export const shareDistance = (metres) => {
  const m = Number(metres);
  if (!(m > 0)) return null;
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(m >= 10000 ? 0 : 1)} km`;
};

/** A rower split, "2:05". */
const fmtSplit = (sec) => {
  const s = Math.round(Number(sec) || 0);
  if (!(s > 0)) return null;
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

const MACHINE_ORDER = ["rower", "indoor_bike"];

/**
 * The headline numbers, each present only when the session has it.
 *
 * @returns {{
 *   title: string, date: string, minutes: number|null, duration: string|null,
 *   distance: string|null, kcal: number|null,
 *   hr: {avg:number, max:number|null}|null, watts: number|null,
 *   wattsMax: number|null, np: number|null, cadence: number|null,
 *   speed: number|null, split: string|null, splitBest: string|null,
 *   strokeRate: number|null, strokes: number|null, machine: string|null,
 *   rating: number|null, ratingLabel: string|null,
 *   zones: number[]|null,
 * }}
 */
export const shareFacts = (session) => {
  const activity = session?.activity || {};
  const summary = session?.telemetry_summary || {};
  const devices = summary.devices || {};
  const machine = MACHINE_ORDER.find((t) => devices[t]) || Object.keys(devices)[0] || null;
  const dev = machine ? devices[machine] : null;

  const minutes = Number(session?.duration_minutes) > 0 ? Math.round(session.duration_minutes) : null;

  const metres = Number(activity.distance_m) > 0
    ? Number(activity.distance_m)
    : Number(dev?.total_distance_m) > 0
      ? Number(dev.total_distance_m)
      : null;

  const hrBlock = summary.hr && summary.hr.avg != null
    ? summary.hr
    : session?.plan_data?.hr?.avg_bpm != null
      ? { avg: session.plan_data.hr.avg_bpm, max: session.plan_data.hr.max_bpm }
      : Number(activity.avg_hr) > 0
        ? { avg: Number(activity.avg_hr), max: Number(activity.max_hr) || null }
        : null;

  const rating = Number(session?.rating);
  const hasRating = Number.isFinite(rating) && rating >= 1 && rating <= 10;
  const kcal = Number(activity.calories) > 0 ? Math.round(activity.calories) : null;
  const zoneSecs = summary.hr?.zones?.seconds;

  return {
    title: titleOf(session),
    date: shareDate(session?.started_at),
    minutes,
    duration: shareMinutes(minutes),
    distance: shareDistance(metres),
    kcal,
    hr: hrBlock ? { avg: Math.round(hrBlock.avg), max: hrBlock.max != null ? Math.round(hrBlock.max) : null } : null,
    watts: dev?.power?.avg > 0 ? Math.round(dev.power.avg) : null,
    wattsMax: dev?.power?.max > 0 ? Math.round(dev.power.max) : null,
    np: machine && summary.np?.[machine] > 0 ? Math.round(summary.np[machine]) : null,
    cadence: dev?.cadence_rpm?.avg > 0 ? Math.round(dev.cadence_rpm.avg) : null,
    speed: dev?.speed_kmh?.avg > 0 ? Math.round(dev.speed_kmh.avg * 10) / 10 : null,
    split: machine === "rower" ? fmtSplit(dev?.pace_sec_per_500m?.avg) : null,
    splitBest: machine === "rower" ? fmtSplit(dev?.pace_sec_per_500m?.min) : null,
    strokeRate: dev?.stroke_rate_spm?.avg > 0 ? Math.round(dev.stroke_rate_spm.avg) : null,
    strokes: dev?.total_strokes > 0 ? Math.round(dev.total_strokes) : null,
    machine,
    rating: hasRating ? Math.round(rating) : null,
    ratingLabel: hasRating ? rpeLabel(rating) : null,
    zones: Array.isArray(zoneSecs) && zoneSecs.some((s) => s > 0) ? zoneSecs : null,
  };
};

/**
 * The message for WhatsApp: the name in *bold* (WhatsApp's markup; other
 * apps just show the asterisks), then the numbers on two plain lines.
 *
 *   *Rowing intervals*
 *   Mon 2 Nov · 38 min · 8.4 km · 512 kcal
 *   151 bpm avg (max 181) · 182 W · 2:05 /500 m
 *   Effort 8/10, Very Hard+
 *   https://khronos.cotlone.com/?s=ab12cd
 *
 * `url` (the workout's share link, or the site) goes last, so WhatsApp shows
 * its preview under the message.
 */
export const formatShareText = (session, { url = null, metrics = null } = {}) => {
  const f = shareFacts(session);
  const join = (parts) => parts.filter(Boolean).join(" · ");
  if (Array.isArray(metrics) && metrics.length) {
    // The user's own numbers, one line per kind (time and distance, heart
    // rate, power…), so an average sits beside its max.
    const stats = shareStats(session, metrics);
    const effort = stats.find((m) => m.key === "effort");
    const numberLines = [];
    for (const m of stats) {
      if (m === effort) continue;
      const prev = numberLines[numberLines.length - 1];
      if (prev && prev.group === m.group) prev.parts.push(m.text);
      else numberLines.push({ group: m.group, parts: [m.text] });
    }
    return [
      `*${f.title}*`,
      f.date,
      ...numberLines.map((l) => join(l.parts)),
      effort && `${effort.text}${f.ratingLabel ? `, ${f.ratingLabel}` : ""}`,
      url,
    ]
      .filter(Boolean)
      .join("\n");
  }
  const lines = [
    `*${f.title}*`,
    join([f.date, f.duration, f.distance, f.kcal && `${f.kcal} kcal`]),
    join([
      f.hr && `${f.hr.avg} bpm avg${f.hr.max ? ` (max ${f.hr.max})` : ""}`,
      f.watts && `${f.watts} W`,
      f.split && `${f.split} /500 m`,
    ]),
    f.rating && `Effort ${f.rating}/10${f.ratingLabel ? `, ${f.ratingLabel}` : ""}`,
    url,
  ];
  return lines.filter(Boolean).join("\n");
};

// Average of the non-null values of `key` in each of `n` equal slices of the
// recording. A slice with nothing in it is null, so the line breaks there
// rather than diving to zero.
const bucketSeries = (samples, key, n) => {
  const first = samples[0].t;
  const span = Math.max(1, samples[samples.length - 1].t + 1 - first);
  const sums = new Array(n).fill(0);
  const counts = new Array(n).fill(0);
  for (const row of samples) {
    const v = row[key];
    if (v == null || !(v > 0)) continue;
    const i = Math.min(n - 1, Math.floor(((row.t - first) / span) * n));
    sums[i] += v;
    counts[i] += 1;
  }
  return sums.map((s, i) => (counts[i] ? s / counts[i] : null));
};

const countPresent = (samples, key) =>
  samples.reduce((n, row) => n + (row[key] != null && row[key] > 0 ? 1 : 0), 0);

const planTypes = (plan) => {
  const types = {};
  const walk = (node) => {
    if (!node || typeof node !== "object") return;
    if (node.id != null && node.type) types[node.id] = node.type;
    (node.children || []).forEach(walk);
  };
  walk(plan);
  return types;
};

// How tall each kind of step stands in the work/rest chart.
export const EFFORT_LEVEL = { work: 1, other: 0.85, warmup: 0.5, cooldown: 0.4, rest: 0.22, transition: 0.12, pause: 0.08 };

// Steps run together into one colour family, so a superset's three exercises
// read as one work block.
const mergePhases = (phases) => {
  const out = [];
  for (const p of phases) {
    const prev = out[out.length - 1];
    if (prev && prev.kind === p.kind) prev.to = p.to;
    else out.push({ ...p, level: EFFORT_LEVEL[p.kind] ?? EFFORT_LEVEL.other });
  }
  return out;
};

// The steps from the step log, as fractions of the session: the shape of a
// session run without a strap or a machine.
const loggedPhases = (plan) => {
  const log = Array.isArray(plan?.phase_log) ? plan.phase_log : [];
  const types = planTypes(plan);
  const steps = log
    .map((e) => ({
      secs: Math.max(0, Number(e.actualDurationSec) || 0),
      kind: phaseKind(types[e.nodeId], e.nodeLabel),
    }))
    .filter((s) => s.secs > 0);
  const total = steps.reduce((a, s) => a + s.secs, 0);
  if (steps.length < 2 || total <= 0) return null;
  let at = 0;
  return mergePhases(
    steps.map((s) => {
      const from = at / total;
      at += s.secs;
      return { from, to: at / total, kind: s.kind };
    }),
  );
};

// The steps laid along the recording (#200), as fractions of it. Empty when
// they can't be placed.
const recordedPhases = (session, rows) => {
  const { segments } = phaseSegments(rows, {
    phaseLog: session?.plan_data?.phase_log || null,
    plan: session?.plan_data || null,
  });
  if (!segments.length) return null;
  const first = rows[0].t;
  const span = Math.max(1, rows[rows.length - 1].t + 1 - first);
  return mergePhases(
    segments.map((seg) => ({
      from: Math.max(0, (seg.start - first) / span),
      to: Math.min(1, (seg.end - first) / span),
      kind: phaseKind(seg.type, seg.label),
    })),
  );
};

/**
 * The card's graph: heart rate when there is enough of it, else power, else
 * the work/rest blocks from the step log. Null when there is nothing to draw.
 *
 * Heart-rate points carry their zone colour when the session saved a zone
 * basis. `phases` are the steps (work, rest, warm-up...) as fractions of the
 * session, for the colour strip under a line or the blocks of an effort chart.
 *
 * @returns {null | {
 *   kind: "hr"|"power"|"effort", unit: string, label: string,
 *   values: Array<number|null>, colours: Array<string|null>|null,
 *   min: number, max: number,
 *   phases: Array<{from:number,to:number,kind:string}>|null,
 * }}
 */
export const shareGraph = (session, samples, n = GRAPH_POINTS) => {
  const rows = Array.isArray(samples) ? samples.filter((r) => r && Number.isFinite(r.t)) : [];
  let kind = null;
  if (rows.length) {
    if (countPresent(rows, "hr") >= MIN_SERIES_SAMPLES) kind = "hr";
    else if (countPresent(rows, "w") >= MIN_SERIES_SAMPLES) kind = "power";
  }

  if (kind) {
    const values = bucketSeries(rows, kind === "hr" ? "hr" : "w", n).map((v) =>
      v == null ? null : Math.round(v),
    );
    const present = values.filter((v) => v != null);
    const zones = session?.telemetry_summary?.hr?.zones;
    const floors = kind === "hr" && zones?.max
      ? zoneFloors({ max: zones.max, resting: zones.resting, method: zones.method })
      : null;
    return {
      kind,
      unit: kind === "hr" ? "bpm" : "W",
      label: kind === "hr" ? "Heart rate" : "Power",
      values,
      colours: floors ? values.map((v) => (v == null ? null : zoneColourFor(v, floors))) : null,
      min: Math.min(...present),
      max: Math.max(...present),
      phases: recordedPhases(session, rows),
    };
  }

  const phases = loggedPhases(session?.plan_data);
  if (!phases) return null;
  return { kind: "effort", unit: "", label: "Work and rest", values: [], colours: null, min: 0, max: 1, phases };
};

/**
 * Every number a session can share, in the order the picker lists them and
 * the share shows them, so related numbers sit together. `group` keeps an
 * average next to its max, and `short` is its name in the picker's row; `value(f)` is the card's text (null when the
 * session doesn't have it) and `text(f)` how it reads in a message.
 */
export const SHARE_METRICS = [
  { key: "time", group: "basics", label: "Time", value: (f) => f.duration, text: (f) => f.duration },
  { key: "distance", group: "basics", label: "Distance", value: (f) => f.distance, text: (f) => f.distance },
  { key: "energy", group: "basics", label: "Energy", value: (f) => f.kcal && `${f.kcal} kcal`, text: (f) => `${f.kcal} kcal` },
  { key: "hr_avg", short: "Avg", group: "hr", label: "Avg HR", value: (f) => f.hr && `${f.hr.avg} bpm`, text: (f) => `${f.hr.avg} bpm avg` },
  { key: "hr_max", short: "Max", group: "hr", label: "Max HR", value: (f) => f.hr?.max && `${f.hr.max} bpm`, text: (f) => `${f.hr.max} max` },
  { key: "power_avg", short: "Avg", group: "power", label: "Avg power", value: (f) => f.watts && `${f.watts} W`, text: (f) => `${f.watts} W avg` },
  { key: "power_max", short: "Max", group: "power", label: "Max power", value: (f) => f.wattsMax && `${f.wattsMax} W`, text: (f) => `${f.wattsMax} max` },
  { key: "power_np", short: "NP", group: "power", label: "Norm. power", value: (f) => f.np && `${f.np} W`, text: (f) => `${f.np} NP` },
  { key: "split_avg", short: "Avg", group: "split", label: "Avg split", value: (f) => f.split && `${f.split} /500 m`, text: (f) => `${f.split} /500 m avg` },
  { key: "split_best", short: "Best", group: "split", label: "Best split", value: (f) => f.splitBest && `${f.splitBest} /500 m`, text: (f) => `${f.splitBest} best` },
  { key: "stroke_rate", short: "Rate", group: "stroke", label: "Stroke rate", value: (f) => f.strokeRate && `${f.strokeRate} spm`, text: (f) => `${f.strokeRate} spm` },
  { key: "strokes", short: "Count", group: "stroke", label: "Strokes", value: (f) => f.strokes && `${f.strokes}`, text: (f) => `${f.strokes} strokes` },
  { key: "cadence", group: "bike", label: "Cadence", value: (f) => f.cadence && `${f.cadence} rpm`, text: (f) => `${f.cadence} rpm` },
  { key: "speed", group: "bike", label: "Avg speed", value: (f) => f.speed && `${f.speed} km/h`, text: (f) => `${f.speed} km/h` },
  { key: "effort", group: "effort", label: "Effort", value: (f) => f.rating && `${f.rating}/10`, text: (f) => `Effort ${f.rating}/10` },
];

const METRIC_BY_KEY = Object.fromEntries(SHARE_METRICS.map((m) => [m.key, m]));

/** The picker's rows: each kind of number under a short heading. */
export const METRIC_ROWS = [
  { label: "Workout", groups: ["basics", "effort"] },
  { label: "Heart", groups: ["hr"] },
  { label: "Power", groups: ["power"] },
  { label: "Split", groups: ["split"] },
  { label: "Strokes", groups: ["stroke"] },
  { label: "Bike", groups: ["bike"] },
];

/** This session's numbers in picker rows: [{ label, metrics }], empty rows left out. */
export const metricRows = (session) => {
  const available = availableMetrics(session);
  return METRIC_ROWS.map((r) => ({ label: r.label, metrics: available.filter((m) => r.groups.includes(m.group)) })).filter(
    (r) => r.metrics.length,
  );
};

/** What's shared when the user hasn't picked: the first three the session has. */
export const DEFAULT_SHARE_METRICS = ["time", "distance", "hr_avg", "power_avg", "split_avg", "energy", "hr_max", "effort"];
const DEFAULT_SHOWN = 3;
// The most a share holds (the card's two rows of three).
const MAX_SHOWN = 6;

/** The metrics this session has a number for, in picker order. */
export const availableMetrics = (session) => {
  const f = shareFacts(session);
  return SHARE_METRICS.filter((m) => m.value(f));
};

/**
 * The tiles for the chosen `metrics` (keys), skipping any this session
 * doesn't have, the first six in the list's order so related numbers sit
 * together. Nothing chosen, or none of the choice present, falls back to
 * the first three defaults the session has (in their own order).
 */
export const shareStats = (session, metrics = null) => {
  const f = shareFacts(session);
  const tile = (m) => ({ key: m.key, group: m.group, label: m.label, value: m.value(f), text: m.text(f) });
  const wanted = new Set(Array.isArray(metrics) ? metrics : []);
  const chosen = SHARE_METRICS.filter((m) => wanted.has(m.key) && m.value(f)).slice(0, MAX_SHOWN);
  if (chosen.length) return chosen.map(tile);
  return DEFAULT_SHARE_METRICS.map((k) => METRIC_BY_KEY[k])
    .filter((m) => m.value(f))
    .slice(0, DEFAULT_SHOWN)
    .map(tile);
};

/** The picture card's data: the facts, the chosen numbers and the graph. */
export const shareCardData = (session, samples, { metrics = null } = {}) => ({
  facts: shareFacts(session),
  stats: shareStats(session, metrics),
  graph: shareGraph(session, samples),
});

/**
 * Whether this session can be sent as a link to the workout itself: a saved
 * session with exercises (the share is a snapshot of its plan).
 */
export const canLinkWorkout = (session) => {
  const id = session?.id;
  if (id == null || String(id).startsWith("local-")) return false;
  return Array.isArray(session?.plan_data?.children) && session.plan_data.children.length > 0;
};

/** "khronos-tabata-row-2-nov.png" */
export const shareFileName = (session) => {
  const slug = `${titleOf(session)} ${shareDate(session?.started_at)}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 48);
  return `khronos-${slug || "workout"}.png`;
};
